import React from 'react';
import { Link } from 'react-router-dom';
import { getOrgBadgeClass } from '../../lib/orgConfig';
import SpeakerImage from './SpeakerImage';
import SpeakerTopics from './SpeakerTopics';

const SpeakerCard = ({ speaker, isFavorite, onToggleFavorite, onClickTopic }) => {
  const handleFavClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onToggleFavorite) onToggleFavorite(speaker.id);
  };

  const upcomingCount = speaker.events_count || 0;

  return (
    <Link
      to={`/speakers/${speaker.id}`}
      className="group block bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
    >
      {/* Header with gradient */}
      <div className="relative h-24 bg-gradient-to-br from-primary via-primary-dark to-secondary">
        {/* Favorite button */}
        <button
          onClick={handleFavClick}
          aria-label={isFavorite ? `Remove ${speaker.name} from favorites` : `Save ${speaker.name} to favorites`}
          className={`absolute top-3 right-3 p-2 rounded-full transition-all z-10 ${
            isFavorite
              ? 'bg-red-500 text-white shadow-md'
              : 'bg-white/20 backdrop-blur-sm text-white hover:bg-red-500'
          }`}
        >
          <svg className="w-4 h-4" fill={isFavorite ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        </button>

        {/* Verified badge */}
        {speaker.is_verified && (
          <div className="absolute top-3 left-3 flex items-center gap-1 px-2 py-1 bg-white/20 backdrop-blur-sm rounded-full">
            <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-xs text-white font-medium">Verified</span>
          </div>
        )}
      </div>

      {/* Circular photo */}
      <div className="relative -mt-12 flex justify-center">
        <SpeakerImage
          speaker={speaker}
          size="lg"
          className="border-4 border-white dark:border-gray-800 shadow-md group-hover:scale-105 transition-transform duration-300"
        />
      </div>

      {/* Content */}
      <div className="p-4 pt-3 text-center">
        <h3 className="font-bold text-base text-gray-900 dark:text-white group-hover:text-primary transition-colors">
          {speaker.name}
        </h3>
        {speaker.title && (
          <p className="text-sm text-primary font-medium mt-0.5">{speaker.title}</p>
        )}

        {/* Church/ministry */}
        {speaker.church_name && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center justify-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            {speaker.church_name}
          </p>
        )}

        {/* Location */}
        {(speaker.city || speaker.state) && (
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 flex items-center justify-center gap-1">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            </svg>
            {[speaker.city, speaker.state].filter(Boolean).join(', ')}
          </p>
        )}

        {/* Organization badge */}
        {speaker.organization && (
          <div className="mt-3">
            <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${getOrgBadgeClass(speaker.organization)}`}>
              {speaker.organization}
            </span>
          </div>
        )}

        {/* Topics */}
        {speaker.topics && speaker.topics.length > 0 && (
          <div className="mt-3 flex justify-center">
            <SpeakerTopics topics={speaker.topics} max={3} onClickTopic={onClickTopic} />
          </div>
        )}

        {/* Event activity */}
        {upcomingCount > 0 && (
          <div className="mt-3 flex items-center justify-center gap-1 text-xs text-gray-500 dark:text-gray-400">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Speaking at {upcomingCount} {upcomingCount === 1 ? 'conference' : 'conferences'}
          </div>
        )}
      </div>
    </Link>
  );
};

export default SpeakerCard;
